import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://arivunest.com';
const BRAND = 'ARIVU NEST';

const DEFAULT_META = {
  title: `${BRAND} | Multidisciplinary Research & Development (MSME Govt. of India)`,
  description:
    'Official Institutional Portal of Arivu Nest – Multidisciplinary Research & Development (Unit of Jjegan Graphers, Ministry of MSME Govt. of India: UDYAM-TN-32-0048110). Academic governance, ISSN journals, ISBN edited volumes, faculty advisory councils, and student research mentorship.',
  robots: 'index, follow'
};

const ROUTE_META = {
  '/': {
    title: `${BRAND} | Multidisciplinary Research & Development (MSME Govt. of India)`,
    description:
      'Official Institutional Portal of Arivu Nest – Multidisciplinary Research & Development (Unit of Jjegan Graphers, Ministry of MSME Govt. of India: UDYAM-TN-32-0048110). Uniting 49+ distinguished professors, researchers, and scientists under a 10-Year Strategic Academic Council (2026–2036).'
  },
  '/faculty': {
    title: `Distinguished Advisory Council & Faculty Directory | ${BRAND} R&D`,
    description:
      'Explore the official directory of 49+ verified professors, scientists, and researchers governing the 10-Year Strategic Academic Council (2026–2036) at Arivu Nest R&D.'
  },
  '/verify': {
    title: `Official Council Member & Certificate Verification | ${BRAND} R&D`,
    description:
      'Real-time central registry to verify authentic appointment credentials, 10-year council tenure (2026–2036), and referral leadership standings for Arivu Nest R&D faculty.'
  },
  '/internships': {
    title: `Student & Scholar Internship Application 2026 | ${BRAND} R&D`,
    description:
      'Apply for official MSME-accredited student internships and project incubation in Generative AI, EdTech, Computational Linguistics, and Research Publications under Dr. S. Sabitha Shunmugapriya.'
  },
  '/research': {
    title: `Research & Publications | ${BRAND} R&D`,
    description:
      'Scholarly archive of 44+ ISBN books, ISSN peer-reviewed journal papers, and multidisciplinary academic publications led by Dr. S. Sabitha Shunmugapriya.'
  },
  '/about': {
    title: `About ${BRAND} | Multidisciplinary Research & Development`,
    description:
      'Learn about Arivu Nest R&D, patronized by Jjegan Graphers under the Ministry of MSME, Govt. of India (UDYAM-TN-32-0048110), in academic collaboration with Kani Rathnam Equality Foundation.'
  },
  '/contact': {
    title: `Contact Executive Directorate | ${BRAND} R&D`,
    description:
      'Contact the Executive Directorate & Statutory Secretariat of Arivu Nest R&D. Inquiries for collegiate partnerships, institutional MoUs, and research symposiums.'
  },
  '/mission': {
    title: `Vision & Strategic Mission | ${BRAND} R&D`,
    description:
      'Strategic vision and academic thrusts of Arivu Nest R&D: elevating collegiate research quality, student mentorship, and global scholarly dissemination.'
  },
  '/courses': {
    title: `Academic Programs & Faculty Development | ${BRAND}`,
    description:
      'Institutional faculty development programs, academic workshops, and research methodology courses conducted by Arivu Nest R&D.'
  },
  '/pricing': {
    title: `Institutional Memberships & R&D Services | ${BRAND}`,
    description:
      'Official 10-Year Academic Council Fellowships (2026–2036), Bilateral Collegiate MoUs, Corporate R&D Consulting, and Student Internships at Arivu Nest R&D (MSME Govt. of India).'
  },
  '/privacy': {
    title: `${BRAND} | Privacy Policy & Data Governance`,
    description:
      'Institutional privacy policy and data governance protocols of Arivu Nest Research and Development.'
  },
  '/terms': {
    title: `${BRAND} | Terms of Service & Institutional Governance`,
    description:
      'Official terms of service, council regulations, and appointment protocols for Arivu Nest R&D.'
  },
  '/login': {
    title: `Sign In | ${BRAND} Portal`,
    description: 'Sign in to the Arivu Nest institutional member portal.',
    robots: 'noindex, nofollow'
  },
  '/signup': {
    title: `Create Account | ${BRAND} Portal`,
    description: 'Create an account on the Arivu Nest institutional portal.',
    robots: 'noindex, nofollow'
  }
};

const NOINDEX_PREFIXES = [
  '/student-dashboard',
  '/admin-dashboard',
  '/instructor-dashboard',
  '/notifications',
  '/notification-preferences',
  '/checkout',
  '/achievements',
  '/settings',
  '/my-courses',
  '/recommendations',
  '/performance',
  '/subscriptions',
  '/certificates',
  '/quiz/',
  '/analytics',
  '/discover',
  '/shop',
  '/profile',
  '/leaderboard'
];

function ensureMetaTag(name, attr = 'name') {
  const selector = attr === 'property' ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let node = document.head.querySelector(selector);

  if (!node) {
    node = document.createElement('meta');
    node.setAttribute(attr, name);
    document.head.appendChild(node);
  }

  return node;
}

function ensureCanonicalTag() {
  let node = document.head.querySelector('link[rel="canonical"]');

  if (!node) {
    node = document.createElement('link');
    node.setAttribute('rel', 'canonical');
    document.head.appendChild(node);
  }

  return node;
}

function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const { pathname } = location;
    const routeMeta = ROUTE_META[pathname] || DEFAULT_META;
    const title = routeMeta.title || DEFAULT_META.title;
    const description = routeMeta.description || DEFAULT_META.description;
    const hasNoIndexPrefix = NOINDEX_PREFIXES.some((prefix) => pathname.startsWith(prefix));
    const robots = hasNoIndexPrefix ? 'noindex, nofollow' : routeMeta.robots || DEFAULT_META.robots;
    const canonicalUrl = `${SITE_URL}${pathname === '/' ? '' : pathname}`;

    document.title = title;

    ensureMetaTag('description').setAttribute('content', description);
    ensureMetaTag('robots').setAttribute('content', robots);
    ensureMetaTag('og:title', 'property').setAttribute('content', title);
    ensureMetaTag('og:description', 'property').setAttribute('content', description);
    ensureMetaTag('og:url', 'property').setAttribute('content', canonicalUrl);
    ensureMetaTag('twitter:title').setAttribute('content', title);
    ensureMetaTag('twitter:description').setAttribute('content', description);
    ensureCanonicalTag().setAttribute('href', canonicalUrl);
  }, [location]);

  return null;
}

export default SeoManager;
